/**
 * @file Paliers de deblocage des fonctionnalites (par stage value) + etapes du tutoriel guide.
 */
import type { UnlockRule } from './types';

export type FeatureId =
  | 'weapons'
  | 'gacha'
  | 'chest'
  | 'skins'
  | 'missions'
  | 'pass'
  | 'daily'
  | 'buffs'
  | 'rebirth'
  | 'stars'
  | 'relics'
  | 'pets'
  | 'fish'
  | 'modeSelect'
  | 'autoMerge'
  | 'hub';

/** stageValue = (stage-1)*10 + (niveau-1). Stage 4-1 = 30. L'original place les etoiles au stage 40 (valeur 390). */
export const FEATURE_UNLOCKS: Record<FeatureId, UnlockRule> = {
  weapons: { kind: 'always' },
  gacha: { kind: 'always' },
  chest: { kind: 'always' },
  daily: { kind: 'always' },
  skins: { kind: 'stageValue', atLeast: 8 },
  missions: { kind: 'stageValue', atLeast: 5 },
  pass: { kind: 'stageValue', atLeast: 10 },
  buffs: { kind: 'stageValue', atLeast: 12 },
  autoMerge: { kind: 'stageValue', atLeast: 15 },
  // Hub social (solo pour l'instant : deplacement manuel, aucun tir) : accessible des le debut.
  hub: { kind: 'always' },
  modeSelect: { kind: 'stageValue', atLeast: 20 },
  rebirth: { kind: 'stageValue', atLeast: 30 },
  relics: { kind: 'stageValue', atLeast: 150 },
  // en meme temps que le mode Monstres (seule source de Pieces de familier) : pas de monnaie gagnee sans usage
  pets: { kind: 'stageValue', atLeast: 20 },
  fish: { kind: 'stageValue', atLeast: 140 },
  stars: { kind: 'stageValue', atLeast: 390 },
};

export interface TutorialStepDef {
  id: string;
  /** Cle i18n du texte d'aide. */
  textKey: string;
  /** Selecteur logique de l'element a mettre en avant (data-tutorial="..."). */
  highlight: string;
  /** Evenement du bus qui termine l'etape (nom de GameEvents). */
  completeOn: 'UpgradeBought' | 'TargetKilled' | 'ScreenOpened' | 'WeaponDrawn' | 'WeaponEquipped';
  /** Nombre d'evenements a voir avant de valider l'etape (1 par defaut) ; affiche dans la bulle via {n}. */
  count?: number;
}

/**
 * Tutoriel : Upgrade -> Abattre quelques arbres -> GunOpen -> GunBuy -> GunEquip.
 * L'etape 2 attendait LevelStarted (bouton Defi de l'original) : le mode Main n'a plus de phase Defi, l'evenement ne venait qu'au niveau
 * suivant (plusieurs minutes) et la bulle restait bloquee. Elle se valide maintenant en abattant `count` arbres.
 */
export const TUTORIAL_STEPS: readonly TutorialStepDef[] = [
  { id: 'upgrade', textKey: 'tutorial.upgrade', highlight: 'upgrade-power', completeOn: 'UpgradeBought' },
  { id: 'chop', textKey: 'tutorial.chop', highlight: 'stage-progress', completeOn: 'TargetKilled', count: 3 },
  { id: 'gunOpen', textKey: 'tutorial.gunOpen', highlight: 'menu-weapons', completeOn: 'ScreenOpened' },
  { id: 'gunBuy', textKey: 'tutorial.gunBuy', highlight: 'draw-normal', completeOn: 'WeaponDrawn' },
  { id: 'gunEquip', textKey: 'tutorial.gunEquip', highlight: 'inventory-first', completeOn: 'WeaponEquipped' },
];
